export const QUEUED_MESSAGE_STATUS = Object.freeze({
  QUEUED: "queued",
  SENDING: "sending",
  FAILED: "failed",
});

export const DEFAULT_MAX_QUEUED_MESSAGES_PER_THREAD = 3;

const normalizeQueue = (queues, threadUuid) => {
  if (!threadUuid) return [];
  const queue = queues?.[threadUuid];
  return Array.isArray(queue) ? queue : [];
};

const withoutEmptyQueue = (queues, threadUuid, queue) => {
  const next = { ...(queues || {}) };
  if (!threadUuid) return next;

  if (queue.length > 0) {
    next[threadUuid] = queue;
  } else {
    delete next[threadUuid];
  }

  return next;
};

export const queuedMessagesForThread = (queues, threadUuid) =>
  normalizeQueue(queues, threadUuid);

export const createQueuedChatMessage = ({
  clientQueueId = null,
  id = null,
  message = "",
  attachments = [],
  createdAt = null,
  extra = {},
} = {}) => {
  const queueId =
    clientQueueId ||
    `queued-${Date.now()}-${Math.random().toString(36).slice(2)}`;

  return {
    id: id || queueId,
    sender: "human",
    message,
    createdAt: createdAt || new Date(),
    shouldAnchor: false,
    attachments: Array.isArray(attachments) ? attachments : [],
    ...extra,
    clientQueueId: queueId,
    queueStatus: QUEUED_MESSAGE_STATUS.QUEUED,
  };
};

export const enqueueQueuedMessageForThread = (
  queues,
  threadUuid,
  item,
  { maxDepth = DEFAULT_MAX_QUEUED_MESSAGES_PER_THREAD } = {},
) => {
  if (!threadUuid) {
    return {
      queues: queues || {},
      enqueued: false,
      reason: "missing_thread",
      depth: 0,
    };
  }

  const current = normalizeQueue(queues, threadUuid);
  if (current.length >= maxDepth) {
    return {
      queues: queues || {},
      enqueued: false,
      reason: "queue_full",
      depth: current.length,
    };
  }

  const nextQueue = current.concat(item);
  return {
    queues: { ...(queues || {}), [threadUuid]: nextQueue },
    enqueued: true,
    reason: null,
    depth: nextQueue.length,
  };
};

export const removeQueuedMessageForThread = (
  queues,
  threadUuid,
  clientQueueId,
) => {
  const current = normalizeQueue(queues, threadUuid);
  const nextQueue = current.filter(
    (item) => item?.clientQueueId !== clientQueueId,
  );

  return {
    queues: withoutEmptyQueue(queues, threadUuid, nextQueue),
    removed: nextQueue.length !== current.length,
  };
};

export const markQueuedMessageForThread = (
  queues,
  threadUuid,
  clientQueueId,
  queueStatus,
  extra = {},
) => {
  const current = normalizeQueue(queues, threadUuid);
  let changed = false;
  const nextQueue = current.map((item) => {
    if (item?.clientQueueId !== clientQueueId) return item;
    changed = true;
    return {
      ...item,
      ...extra,
      queueStatus,
    };
  });

  return {
    queues: changed ? { ...(queues || {}), [threadUuid]: nextQueue } : (queues || {}),
    changed,
  };
};

export const updateQueuedMessageForThread = (
  queues,
  threadUuid,
  clientQueueId,
  patchOrUpdater = {},
) => {
  const current = normalizeQueue(queues, threadUuid);
  let changed = false;
  const nextQueue = current.map((item) => {
    if (item?.clientQueueId !== clientQueueId) return item;

    const patch =
      typeof patchOrUpdater === "function"
        ? patchOrUpdater(item)
        : patchOrUpdater;
    if (!patch || typeof patch !== "object") return item;

    changed = true;
    return {
      ...item,
      ...patch,
    };
  });

  return {
    queues: changed ? { ...(queues || {}), [threadUuid]: nextQueue } : (queues || {}),
    changed,
  };
};

export const shiftQueuedMessageForThread = (
  queues,
  threadUuid,
  expectedClientQueueId = null,
) => {
  const current = normalizeQueue(queues, threadUuid);
  const first = current[0] || null;
  if (!first) {
    return {
      queues: queues || {},
      shifted: null,
    };
  }

  if (
    expectedClientQueueId &&
    first.clientQueueId !== expectedClientQueueId
  ) {
    return {
      queues: queues || {},
      shifted: null,
    };
  }

  return {
    queues: withoutEmptyQueue(queues, threadUuid, current.slice(1)),
    shifted: first,
  };
};

export const moveQueuedMessagesForThread = (
  queues,
  fromThreadUuid,
  toThreadUuid,
) => {
  if (!fromThreadUuid || !toThreadUuid || fromThreadUuid === toThreadUuid) {
    return queues || {};
  }

  const fromQueue = normalizeQueue(queues, fromThreadUuid);
  if (fromQueue.length === 0) return queues || {};

  const toQueue = normalizeQueue(queues, toThreadUuid);
  const next = { ...(queues || {}) };
  next[toThreadUuid] = toQueue.concat(fromQueue);
  delete next[fromThreadUuid];
  return next;
};

export const clearQueuedMessagesForThread = (queues, threadUuid) => {
  if (!threadUuid || !Object.prototype.hasOwnProperty.call(queues || {}, threadUuid)) {
    return queues || {};
  }

  const next = { ...(queues || {}) };
  delete next[threadUuid];
  return next;
};
